/** The book: everything started, and what state each one is in. */
import { api } from "../../lib/api.ts";
import { Link } from "../../lib/router.tsx";
import { useAsync } from "../../lib/useAsync.ts";
import { Empty, Loading, PageHead, Problem, Tiles } from "../../components/Page.tsx";
import type { ApplicationRow } from "./types.ts";

export function Applications() {
  const applications = useAsync(() => api.get<{ applications: ApplicationRow[] }>("/api/applications"), []);
  const rows = applications.data?.applications ?? [];

  return (
    <div className="stack">
      <PageHead
        title="Applications"
        sub="Each one is filled in against the product version it was started on."
        actions={<Link to="/catalogue" className="btn btn-primary">Start a new one</Link>}
      />

      <Tiles
        tiles={[
          { label: "Applications", value: rows.length, tone: "accent" },
          { label: "In progress", value: rows.filter((row) => row.status === "draft").length, tone: "warn" },
          { label: "Submitted", value: rows.filter((row) => row.status === "submitted").length, tone: "ok" },
          { label: "Products used", value: new Set(rows.map((row) => row.productCode)).size, tone: "info" },
        ]}
      />

      <Problem error={applications.error} />

      {applications.loading && !applications.data ? (
        <Loading what="applications" />
      ) : rows.length === 0 ? (
        <Empty title="Nothing started yet">
          Pick something from the <Link to="/catalogue">catalogue</Link> to begin.
        </Empty>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Reference</th><th>Product</th><th>Applicant</th><th>State</th><th>Last touched</th></tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td><Link to={`/applications/${row.id}`}>{row.reference}</Link></td>
                    <td>
                      {row.productName}
                      <div className="small muted">v{row.version}</div>
                    </td>
                    <td>{row.label ?? <span className="muted">—</span>}</td>
                    <td>
                      {row.status === "submitted"
                        ? <span className="chip chip-ok">Submitted</span>
                        : <span className="chip chip-warn">In progress</span>}
                    </td>
                    <td className="small muted">{new Date(row.updatedAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
